/**
 * Parse Suggestions Utility
 *
 * Extracts tap-to-reply response controls from assistant content.
 *
 * Primary responsibilities:
 * - Parse suggestion markers from text content
 * - Parse user questionnaire markers from text content
 * - Remove markers from visible content
 * - Return cleaned content and response-control metadata
 */

import type {
  UserQuestionnaire,
  UserQuestionnaireMode,
  UserQuestionnaireOption,
  UserQuestionnaireQuestion
} from '@/types/user-questionnaire'

export interface ParsedSuggestions {
  content: string
  suggestions: string[]
  questionnaire: UserQuestionnaire | null
}

export type AssistantResponseControlMetadata = Record<string, unknown> & {
  suggestions?: string[]
  questionnaire?: UserQuestionnaire
}

const QUESTIONNAIRE_MARKER_PATTERN =
  /\\?\[\s*USER[_\s-]*QUESTIONN?AIRE\s*(?::|\])?\s*/i
const QUESTIONNAIRE_CLOSING_TAG_PATTERN =
  /^\s*\[\/\s*USER[_\s-]*QUESTIONN?AIRE\s*\]/i
const RESPONSE_CONTROL_MARKER_PATTERN =
  /\\?\[\s*(?:SUGGESTIONS|USER[_\s-]*QUESTIONN?AIRE)/i
const MAX_QUESTIONNAIRE_OPTIONS = 8
const MAX_QUESTIONNAIRE_QUESTIONS = 8
const MAX_PROMPT_LENGTH = 240
const MAX_LABEL_LENGTH = 90
const MAX_REPLY_LENGTH = 600

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function cleanText(value: unknown, maxLength: number) {
  return typeof value === 'string' ? value.trim().slice(0, maxLength) : ''
}

function cleanFirstText(
  value: Record<string, unknown>,
  keys: string[],
  maxLength: number
) {
  for (const key of keys) {
    const text = cleanText(value[key], maxLength)

    if (text) {
      return text
    }
  }

  return ''
}

function normalizeOption(value: unknown, index: number): UserQuestionnaireOption | null {
  if (typeof value === 'string') {
    const label = cleanText(value, MAX_LABEL_LENGTH)
    return label ? { id: `option-${index + 1}`, label } : null
  }

  if (!isRecord(value)) {
    return null
  }

  const label = cleanFirstText(
    value,
    ['label', 'text', 'title', 'name', 'value'],
    MAX_LABEL_LENGTH
  )
  if (!label) {
    return null
  }

  const id =
    cleanFirstText(value, ['id', 'key', 'value'], 64) ||
    label.toLowerCase().replace(/[^a-z0-9]+/g, '-')
  const reply = cleanFirstText(
    value,
    ['reply', 'response', 'message', 'content'],
    MAX_REPLY_LENGTH
  )

  return {
    id: id || `option-${index + 1}`,
    label,
    ...(reply ? { reply } : {})
  }
}

function normalizeQuestionnaireMode(
  value: unknown,
  source?: Record<string, unknown>
): UserQuestionnaireMode {
  if (
    source?.multiple === true ||
    source?.multi === true ||
    source?.allowMultiple === true
  ) {
    return 'multi'
  }

  const mode = (
    cleanText(value, 64) ||
    (source
      ? cleanFirstText(source, ['type', 'inputType', 'selection'], 64)
      : '')
  )
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

  if (
    mode === 'multi' ||
    mode === 'multi_select' ||
    mode === 'multi_choice' ||
    mode === 'multiple' ||
    mode === 'multiple_select' ||
    mode === 'multiple_choice' ||
    mode === 'select_multiple' ||
    mode === 'checkbox' ||
    mode === 'checkboxes'
  ) {
    return 'multi'
  }

  if (
    mode === 'yes_no' ||
    mode === 'yesno' ||
    mode === 'boolean' ||
    mode === 'confirm'
  ) {
    return 'yes_no'
  }

  if (
    mode === 'approval' ||
    mode === 'approve' ||
    mode === 'permission'
  ) {
    return 'approval'
  }

  return 'single'
}

function getDefaultOptions(mode: UserQuestionnaireMode): UserQuestionnaireOption[] {
  if (mode === 'yes_no') {
    return [
      { id: 'yes', label: 'Yes', reply: 'Yes.' },
      { id: 'no', label: 'No', reply: 'No.' }
    ]
  }

  if (mode === 'approval') {
    return [
      { id: 'approve', label: 'Approve', reply: 'I approve. Continue.' },
      { id: 'decline', label: 'Decline', reply: 'I do not approve.' }
    ]
  }

  return []
}

function normalizeQuestionnaireQuestion(
  value: unknown,
  index: number
): UserQuestionnaireQuestion | null {
  if (!isRecord(value)) {
    return null
  }

  const prompt = cleanFirstText(
    value,
    ['prompt', 'question', 'text', 'title'],
    MAX_PROMPT_LENGTH
  )
  const mode = normalizeQuestionnaireMode(value.mode, value)
  const rawOptions =
    Array.isArray(value.options)
      ? value.options
      : Array.isArray(value.choices)
        ? value.choices
        : Array.isArray(value.answers)
          ? value.answers
          : Array.isArray(value.items)
            ? value.items
            : []
  const options = rawOptions
    .map(normalizeOption)
    .filter((option): option is UserQuestionnaireOption => Boolean(option))
    .slice(0, MAX_QUESTIONNAIRE_OPTIONS)
  const normalizedOptions = options.length > 0 ? options : getDefaultOptions(mode)

  if (!prompt || normalizedOptions.length === 0) {
    return null
  }

  const id = cleanText(value.id, 80) || `question-${index + 1}`
  const submitLabel = cleanText(value.submitLabel, MAX_LABEL_LENGTH)
  const skipLabel = cleanText(value.skipLabel, MAX_LABEL_LENGTH)
  const skipReply = cleanText(value.skipReply, MAX_REPLY_LENGTH)

  return {
    id,
    prompt,
    mode,
    options: normalizedOptions,
    ...(value.optional === true ? { optional: true } : {}),
    ...(submitLabel ? { submitLabel } : {}),
    ...(skipLabel ? { skipLabel } : {}),
    ...(skipReply ? { skipReply } : {})
  }
}

function normalizeQuestionnaire(value: unknown): UserQuestionnaire | null {
  if (!isRecord(value)) {
    return null
  }

  const questions = (Array.isArray(value.questions) ? value.questions : [])
    .map(normalizeQuestionnaireQuestion)
    .filter((question): question is UserQuestionnaireQuestion => Boolean(question))
    .slice(0, MAX_QUESTIONNAIRE_QUESTIONS)

  if (questions.length > 0) {
    const firstQuestion = questions[0]
    const id =
      cleanText(value.id, 80) ||
      `questionnaire-${firstQuestion.prompt.slice(0, 32)}`
    const nextLabel = cleanText(value.nextLabel, MAX_LABEL_LENGTH)
    const backLabel = cleanText(value.backLabel, MAX_LABEL_LENGTH)
    const finishLabel = cleanText(value.finishLabel, MAX_LABEL_LENGTH)

    return {
      ...firstQuestion,
      id,
      questions,
      ...(nextLabel ? { nextLabel } : {}),
      ...(backLabel ? { backLabel } : {}),
      ...(finishLabel ? { finishLabel } : {})
    }
  }

  return normalizeQuestionnaireQuestion(value, 0)
}

function findJsonObjectEnd(content: string, startIndex: number) {
  let depth = 0
  let inString = false
  let escaped = false

  for (let index = startIndex; index < content.length; index += 1) {
    const char = content[index]

    if (escaped) {
      escaped = false
      continue
    }

    if (char === '\\') {
      escaped = true
      continue
    }

    if (char === '"') {
      inString = !inString
      continue
    }

    if (inString) {
      continue
    }

    if (char === '{') {
      depth += 1
      continue
    }

    if (char === '}') {
      depth -= 1
      if (depth === 0) {
        return index
      }
    }
  }

  return -1
}

function findQuestionnaireMarker(content: string) {
  const markerMatch = QUESTIONNAIRE_MARKER_PATTERN.exec(content)
  if (!markerMatch) {
    return null
  }

  const markerStart = markerMatch.index
  const markerPrefixEnd = markerStart + markerMatch[0].length
  const jsonStart = content.indexOf('{', markerPrefixEnd)
  if (jsonStart === -1) {
    return {
      markerStart,
      markerEnd: content.length,
      questionnaire: null
    }
  }

  const jsonEnd = findJsonObjectEnd(content, jsonStart)
  if (jsonEnd === -1) {
    return {
      markerStart,
      markerEnd: content.length,
      questionnaire: null
    }
  }

  let markerEnd = jsonEnd + 1
  while (/\s/.test(content[markerEnd] ?? '')) {
    markerEnd += 1
  }

  const closingTag = content.slice(markerEnd).match(QUESTIONNAIRE_CLOSING_TAG_PATTERN)
  if (closingTag?.[0]) {
    markerEnd += closingTag[0].length
  } else if (content[markerEnd] === ']') {
    markerEnd += 1
  } else if (content.slice(markerPrefixEnd, jsonStart).includes(']')) {
    // `[USER_QUESTIONNAIRE] {...}` is accepted without a trailing bracket.
  } else {
    const fallbackEnd = content.indexOf(']', jsonEnd + 1)

    return {
      markerStart,
      markerEnd: fallbackEnd === -1 ? content.length : fallbackEnd + 1,
      questionnaire: null
    }
  }

  try {
    return {
      markerStart,
      markerEnd,
      questionnaire: normalizeQuestionnaire(JSON.parse(content.slice(jsonStart, jsonEnd + 1)))
    }
  } catch {
    return {
      markerStart,
      markerEnd,
      questionnaire: null
    }
  }
}

export function mergeAssistantResponseControlMetadata(
  metadata: AssistantResponseControlMetadata | null | undefined,
  parsed: ParsedSuggestions
): AssistantResponseControlMetadata | null {
  const next: AssistantResponseControlMetadata = { ...(metadata ?? {}) }

  delete next.suggestions
  delete next.questionnaire

  if (parsed.suggestions.length > 0) {
    next.suggestions = parsed.suggestions
  }

  if (parsed.questionnaire) {
    next.questionnaire = parsed.questionnaire
  }

  return Object.keys(next).length > 0 ? next : null
}

export function stripAssistantResponseControlMarkers(content: string | null | undefined) {
  return parseSuggestions(content || '').content
}

export function hasAssistantResponseControlMarker(content: string) {
  return RESPONSE_CONTROL_MARKER_PATTERN.test(content)
}

/**
 * Text sent when the user taps a suggestion link.
 * Strips a trailing `?` / `？` so "Rebuild the plan?" → "Rebuild the plan".
 */
export function suggestionSendText(suggestion: string) {
  return suggestion
    .trim()
    .replace(/[?？]+\s*$/u, '')
    .trim()
}

/**
 * Parse suggestions from content using marker format
 * Format: [SUGGESTIONS: "question 1", "question 2", "question 3"]
 * 
 * Also handles partial markers during streaming by removing incomplete markers
 * from the end of content (e.g., "[SUGGESTIONS: "q" would be removed)
 */
export function parseSuggestions(content: string): ParsedSuggestions {
  // Primary pattern to match well‑formatted suggestions:
  // [SUGGESTIONS: "q1", "q2", "q3"]
  const strictPattern = /\[SUGGESTIONS:\s*((?:"[^"]*"(?:\s*,\s*"[^"]*")*)?)\]/gi
  // Fallback pattern to catch ANY suggestions block, even if formatting is off:
  // [SUGGESTIONS: option 1, option 2] or with newlines, missing quotes, etc.
  const loosePattern = /\[SUGGESTIONS:\s*([\s\S]*?)\]/gi

  const suggestions: string[] = []
  let cleanedContent = content
  let questionnaire: UserQuestionnaire | null = null

  // Collect all markers to remove
  const markersToRemove: string[] = []

  const addSuggestion = (value: string) => {
    const suggestion = value.trim()
    if (suggestion) {
      suggestions.push(suggestion)
    }
  }

  const parseInnerSuggestions = (inner: string, isStrict: boolean) => {
    if (!inner) return

    if (isStrict) {
      // Strict mode: suggestions MUST be in double quotes
      const quotedStrings = inner.match(/"([^"]*)"/g)
      if (quotedStrings) {
        for (const quoted of quotedStrings) {
          addSuggestion(quoted.slice(1, -1))
        }
      }
      return
    }

    // Loose mode: split on commas, strip surrounding quotes/punctuation
    const parts = inner
      .split(',')
      .map((part) =>
        part
          .trim()
          // Remove surrounding quotes or punctuation
          .replace(/^["'“”‘’\[\]\(\)\-–\u2014]+/, '')
          .replace(/["'“”‘’\[\]\)\.!\?]+$/, '')
          .trim()
      )
      .filter((part) => part.length > 0)

    for (const part of parts) {
      addSuggestion(part)
    }
  }

  const hasMarker = content.includes('[SUGGESTIONS')

  // Helper to run a given pattern and parse matches
  const runPattern = (pattern: RegExp, isStrict: boolean) => {
    const matches = Array.from(content.matchAll(pattern))
    for (const match of matches) {
      markersToRemove.push(match[0])
      parseInnerSuggestions(match[1], isStrict)
    }
  }

  // First pass: strict pattern
  runPattern(strictPattern, true)

  // If strict parsing produced no suggestions but a marker exists (for example,
  // because of nested quotes like "today only"), fall back to loose parsing.
  if (hasMarker && suggestions.length === 0) {
    markersToRemove.length = 0
    cleanedContent = content
    runPattern(loosePattern, false)
  }

  // Remove all markers from content at once
  for (const marker of markersToRemove) {
    cleanedContent = cleanedContent.replace(marker, '').trim()
  }

  const questionnaireMarker = findQuestionnaireMarker(cleanedContent)
  if (questionnaireMarker) {
    questionnaire = questionnaireMarker.questionnaire
    cleanedContent = [
      cleanedContent.slice(0, questionnaireMarker.markerStart),
      cleanedContent.slice(questionnaireMarker.markerEnd)
    ]
      .join('')
      .trim()
  }

  // Handle partial markers during streaming - remove incomplete markers from the end
  // Check if content ends with a potential marker start (but not a complete marker)
  const partialMarker = cleanedContent.match(
    /\\?\[\s*(?:SUGGESTIONS|USER[_\s-]*QUESTIONN?AIRE)[\s\S]*$/i
  )
  if (partialMarker?.index != null) {
    cleanedContent = cleanedContent.substring(0, partialMarker.index).trim()
  }

  return {
    content: cleanedContent,
    suggestions,
    questionnaire
  }
}
